import { useOktaAuth } from '@okta/okta-react';
import {
  initAnalytics,
  setAmplitudeEventEnrichmentProperties,
  setEventProperties,
  setSharedProperties,
  setUserProperties,
} from '@wmgone/analytics';
import { useContext, useEffect } from 'react';

import { env } from '~/lib/env';
import { useUser } from '~/modules/auth/hooks/useUser';
import { BrandContext } from '~/modules/branding';
import { checkUserForOptOut } from '~/modules/monitoring/analytics/utils/checkUserForOptOut';

import { initContainerAnalytics } from './useAnalytics';
import { getAmplitudeEventEnrichmentProperties } from './utils/getAmplitudeEventEnrichmentProperties';
import { getDefaultEventProperties } from './utils/getDefaultEventProperties';
import { getDefaultSharedProperties } from './utils/getDefaultSharedProperties';
import { getDefaultUserProperties } from './utils/getDefaultUserProperties';

/**
 * Component used in the Container to initialize the Analytics and  set global
 * analytics user properties.
 */
export function Analytics() {
  const user = useUser();
  const brand = useContext(BrandContext);
  const { authState } = useOktaAuth();

  useEffect(() => {
    // We only initialize the analytics module once we have a fully logged in
    // and authenticated user.
    if (authState?.isAuthenticated) {
      // Initialize the base analytics module.
      initAnalytics(env('VITE_AMPLITUDE_API_KEY', false));

      checkUserForOptOut(user);

      // Properties set only on the User.
      setUserProperties(getDefaultUserProperties(brand));

      // Properties tracked only at the Event level for each Event.
      setEventProperties(getDefaultEventProperties(brand));

      // Properties set on the User and also tracked with each Event.
      setSharedProperties(getDefaultSharedProperties(user));

      // Properties used to enrich the Amplitude events.
      setAmplitudeEventEnrichmentProperties(
        getAmplitudeEventEnrichmentProperties(brand, user),
      );

      initContainerAnalytics();
    }
  }, [authState?.isAuthenticated, brand, user]);

  return null;
}

Analytics.displayName = 'Analytics';
